import { useEffect, useRef } from 'react';

// A handheld USB/HID barcode scanner acts as a keyboard: it "types" the
// barcode's characters into whatever currently has focus, followed by a
// terminator key (almost always Enter, sometimes Tab). When nothing on the
// page is focused, those keydown events land on document.body and bubble to
// window — so no visible textfield is required to capture them, just a
// global keydown listener.
//
// What distinguishes a scan from a person typing is speed: a scanner emits
// characters only a few ms apart, versus 100ms+ between a human's keystrokes.
// So the buffer resets whenever the gap since the previous key exceeds
// CHAR_GAP_MS, and only a buffer that reached MIN_LENGTH by the time the
// terminator arrives is treated as a scan.
const CHAR_GAP_MS = 50;
const MIN_LENGTH = 3;

const isEditableTarget = (target) =>
  !!target &&
  (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

/**
 * Listens globally for hardware barcode-scanner input (keyboard-wedge mode)
 * and invokes `onScan(code)` once a fast-typed sequence is terminated by
 * Enter/Tab. Ignored while a real input/textarea/contenteditable has focus,
 * so it never intercepts the operator's own typing (e.g. ManualRxDialog,
 * AddDrugByLotDialog). Pass `{ enabled: false }` to detach the listener.
 */
const useHardwareScanner = (onScan, { enabled = true } = {}) => {
  const handlerRef = useRef(onScan);
  handlerRef.current = onScan;

  useEffect(() => {
    if (!enabled) return undefined;

    let buffer = '';
    let lastKeyTime = 0;

    const handleKeyDown = (e) => {
      if (isEditableTarget(e.target)) return;

      const now = Date.now();
      if (now - lastKeyTime > CHAR_GAP_MS) buffer = '';
      lastKeyTime = now;

      if (e.key === 'Enter' || e.key === 'Tab') {
        const code = buffer;
        buffer = '';
        if (code.length >= MIN_LENGTH) {
          e.preventDefault();
          handlerRef.current?.(code);
        }
        return;
      }

      if (e.key.length === 1) buffer += e.key;
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
};

export default useHardwareScanner;
